'use client';

import type { DehydratedState } from '@tanstack/react-query';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { HardAccessGate } from '@/components/hard-access-gate';
import { LeadCaptureSync } from '@/components/lead-capture-sync';
import { OfferGateOverlay } from '@/components/offer/offer-gate-overlay';
import { TrialActivationTracker } from '@/components/trial-activation-tracker';
import { UserProvider } from '@/contexts/user-context';
import { PwaProvider } from '@/hooks/use-pwa'; // Import the provider
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { CustomToaster } from './ui/custom-toast';

export default function Providers({
  children,
  dehydratedState,
}: {
  children: React.ReactNode;
  dehydratedState?: DehydratedState;
}) {
  const pathname = usePathname();

  // Skip offer overlay on admin, checkout, and premium flows where it would be disruptive
  const shouldShowOfferOverlay = useMemo(() => {
    if (!pathname) {
      return true;
    }
    const exemptSegments = [
      '/admin',
      '/checkout',
      '/premium',
      '/offer',
      '/credits',
      '/home',
    ];
    return !exemptSegments.some((seg) => pathname.includes(seg));
  }, [pathname]);

  const offerContext = useMemo(() => {
    if (!pathname) {
      return 'default' as const;
    }
    if (pathname.includes('compat')) {
      return 'compatibility' as const;
    }
    if (pathname.includes('tarot')) {
      return 'tarot' as const;
    }
    if (pathname.includes('money') || pathname.includes('finance')) {
      return 'money' as const;
    }
    return 'default' as const;
  }, [pathname]);

  return (
    <PwaProvider>
      {' '}
      {/* Wrap everything in the PwaProvider */}
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <UserProvider>
              <HardAccessGate>{children}</HardAccessGate>
              <CustomToaster richColors />
              <LeadCaptureSync />
              <TrialActivationTracker />
              {shouldShowOfferOverlay ? <OfferGateOverlay context={offerContext} /> : null}
            </UserProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </trpc.Provider>
    </PwaProvider>
  );
}
